import { useState } from 'react';
import {
  useAutopilotConfig,
  useAutopilotActivity,
  saveAutopilotConfig,
  overrideAutopilotDecision,
  type AutopilotConfig,
  type AutopilotDecision,
} from '../../hooks/useSecOps';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400',
  high: 'bg-orange-500/20 text-orange-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  low: 'bg-blue-500/20 text-blue-400',
  info: 'bg-zinc-700 text-zinc-400',
};

const DECISION_COLORS: Record<string, string> = {
  close_fp: 'bg-green-500/20 text-green-400',
  escalate: 'bg-red-500/20 text-red-400',
  investigate: 'bg-yellow-500/20 text-yellow-400',
  hold: 'bg-zinc-700 text-zinc-400',
};

const STATUS_COLORS: Record<string, string> = {
  applied: 'bg-green-500/20 text-green-400',
  overridden: 'bg-orange-500/20 text-orange-400',
  pending_review: 'bg-blue-500/20 text-blue-400',
};

const DECISION_LABELS: Record<string, string> = {
  close_fp: 'Close (FP)',
  escalate: 'Escalate',
  investigate: 'Investigate',
  hold: 'Hold',
};

function StatCard({ label, value, accent }: { label: string; value: number | string; accent: string }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
      <p className="text-3xl font-bold text-gray-100">{value}</p>
      <p className="text-sm text-gray-400 mt-1">{label}</p>
      <div className={`mt-3 h-1 w-12 rounded ${accent}`} />
    </div>
  );
}

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-zinc-700 text-zinc-400'}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

export default function SOCAutopilotPage() {
  const { data: configResp, loading: configLoading, refresh: refreshConfig } = useAutopilotConfig();
  const { data: activityResp, loading: activityLoading, refresh: refreshActivity } = useAutopilotActivity();

  const config = configResp?.data;
  const activity = activityResp?.data;
  const stats = activity?.stats;
  const decisions = activity?.decisions ?? [];

  const [saving, setSaving] = useState(false);
  const [overrideModal, setOverrideModal] = useState<AutopilotDecision | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overriding, setOverriding] = useState(false);

  const [localConfig, setLocalConfig] = useState<Partial<AutopilotConfig> | null>(null);
  const effectiveConfig = localConfig ?? config;

  const handleToggle = async (field: keyof AutopilotConfig, value: unknown) => {
    const updated = { ...effectiveConfig, [field]: value };
    setLocalConfig(updated as AutopilotConfig);
  };

  const handleSave = async () => {
    if (!effectiveConfig) return;
    setSaving(true);
    try {
      await saveAutopilotConfig(effectiveConfig);
      setLocalConfig(null);
      refreshConfig();
    } finally {
      setSaving(false);
    }
  };

  const handleOverride = async (action: 'approve' | 'reject') => {
    if (!overrideModal) return;
    setOverriding(true);
    try {
      await overrideAutopilotDecision({
        decision_id: overrideModal.id,
        action,
        reason: overrideReason,
      });
      setOverrideModal(null);
      setOverrideReason('');
      refreshActivity();
    } finally {
      setOverriding(false);
    }
  };

  if (configLoading && !config) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto bg-[#09090b] min-h-screen">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-zinc-800 rounded w-72" />
          <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-zinc-800 rounded-lg" />)}</div>
          <div className="h-96 bg-zinc-800 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto bg-[#09090b] min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">AI Autopilot</h1>
          <p className="text-gray-400 text-sm mt-1">SOC-as-a-Service: AI Tier 1 analyst automates alert triage</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Auto-closed Today" value={stats.auto_closed_today} accent="bg-green-500" />
          <StatCard label="Escalated Today" value={stats.escalated_today} accent="bg-red-500" />
          <StatCard label="Pending Review" value={stats.pending_review} accent="bg-blue-500" />
          <StatCard label="Override Rate" value={`${stats.override_rate}%`} accent="bg-orange-500" />
        </div>
      )}

      {/* Configuration */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-100 mb-4">Autopilot Configuration</h2>
        {effectiveConfig && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-200">Enable Autopilot</p>
                <p className="text-xs text-gray-500">Allow AI to automatically triage new alerts</p>
              </div>
              <button
                onClick={() => handleToggle('enabled', !effectiveConfig.enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${effectiveConfig.enabled ? 'bg-indigo-600' : 'bg-zinc-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${effectiveConfig.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-200 block mb-2">
                Confidence Threshold: {effectiveConfig.confidence_threshold}%
              </label>
              <input
                type="range"
                min={0}
                max={100}
                value={effectiveConfig.confidence_threshold ?? 85}
                onChange={(e) => handleToggle('confidence_threshold', Number(e.target.value))}
                className="w-full accent-indigo-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0% (aggressive)</span>
                <span>100% (conservative)</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-200">Auto-close False Positives</p>
                <p className="text-xs text-gray-500">Automatically close alerts AI identifies as false positives</p>
              </div>
              <button
                onClick={() => handleToggle('auto_close_false_positives', !effectiveConfig.auto_close_false_positives)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${effectiveConfig.auto_close_false_positives ? 'bg-indigo-600' : 'bg-zinc-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${effectiveConfig.auto_close_false_positives ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-200">Auto-escalate Confirmed Threats</p>
                <p className="text-xs text-gray-500">Automatically escalate alerts AI confirms as threats</p>
              </div>
              <button
                onClick={() => handleToggle('auto_escalate_confirmed', !effectiveConfig.auto_escalate_confirmed)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${effectiveConfig.auto_escalate_confirmed ? 'bg-indigo-600' : 'bg-zinc-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${effectiveConfig.auto_escalate_confirmed ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-200 block mb-2">Excluded Categories</label>
              <p className="text-xs text-gray-500 mb-1">Alert categories the AI should skip (one per line)</p>
              <textarea
                value={(effectiveConfig.excluded_categories ?? []).join('\n')}
                onChange={(e) => handleToggle('excluded_categories', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                rows={3}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:border-indigo-500 focus:outline-none"
                placeholder="e.g. test-alerts&#10;maintenance&#10;info-only"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-200 block mb-2">Human Review Required Above</label>
              <select
                value={effectiveConfig.human_review_required_above ?? 'high'}
                onChange={(e) => handleToggle('human_review_required_above', e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:border-indigo-500 focus:outline-none"
              >
                <option value="high">High severity and above</option>
                <option value="critical">Critical severity only</option>
              </select>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        )}
      </div>

      {/* Activity Feed */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-100">AI Triage Activity</h2>
          <span className="text-xs text-gray-500">{activity?.total ?? 0} total decisions</span>
        </div>
        {activityLoading && decisions.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading activity...</div>
        ) : decisions.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No autopilot decisions yet. Enable autopilot to start AI triage.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-zinc-800">
                  <th className="px-6 py-3 font-medium">Alert</th>
                  <th className="px-6 py-3 font-medium">Severity</th>
                  <th className="px-6 py-3 font-medium">Decision</th>
                  <th className="px-6 py-3 font-medium">Confidence</th>
                  <th className="px-6 py-3 font-medium">Reasoning</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Time</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {decisions.map((d) => (
                  <tr key={d.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="px-6 py-3 text-gray-200 max-w-[200px] truncate">{d.alert_title ?? d.alert_id}</td>
                    <td className="px-6 py-3"><Badge value={d.alert_severity ?? 'unknown'} colors={SEVERITY_COLORS} /></td>
                    <td className="px-6 py-3"><Badge value={d.decision} colors={DECISION_COLORS} /></td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-zinc-800 rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full transition-all"
                            style={{
                              width: `${d.confidence}%`,
                              backgroundColor: d.confidence >= 80 ? '#22c55e' : d.confidence >= 60 ? '#eab308' : '#ef4444',
                            }}
                          />
                        </div>
                        <span className="text-gray-400 text-xs">{d.confidence}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-gray-400 max-w-[250px] truncate" title={d.reasoning}>{d.reasoning}</td>
                    <td className="px-6 py-3"><Badge value={d.status} colors={STATUS_COLORS} /></td>
                    <td className="px-6 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(d.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-3">
                      {d.status === 'pending_review' && (
                        <button
                          onClick={() => setOverrideModal(d)}
                          className="text-indigo-400 hover:text-indigo-300 text-xs font-medium"
                        >
                          Review
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Override Modal */}
      {overrideModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setOverrideModal(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-100 mb-4">Review AI Decision</h3>
            <div className="space-y-3 mb-4">
              <div>
                <span className="text-gray-500 text-xs">Alert:</span>
                <p className="text-gray-200 text-sm">{overrideModal.alert_title ?? overrideModal.alert_id}</p>
              </div>
              <div className="flex gap-4">
                <div>
                  <span className="text-gray-500 text-xs">AI Decision:</span>
                  <p className="mt-0.5"><Badge value={overrideModal.decision} colors={DECISION_COLORS} /></p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Confidence:</span>
                  <p className="text-gray-200 text-sm mt-0.5">{overrideModal.confidence}%</p>
                </div>
              </div>
              <div>
                <span className="text-gray-500 text-xs">AI Reasoning:</span>
                <p className="text-gray-300 text-sm mt-0.5">{overrideModal.reasoning}</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-gray-400 text-xs block mb-1">Override Reason (optional)</label>
              <textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:border-indigo-500 focus:outline-none"
                placeholder="Why are you overriding this decision?"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setOverrideModal(null)}
                className="text-gray-400 hover:text-gray-300 text-sm px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={() => handleOverride('reject')}
                disabled={overriding}
                className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {overriding ? '...' : 'Reject'}
              </button>
              <button
                onClick={() => handleOverride('approve')}
                disabled={overriding}
                className="bg-green-600 hover:bg-green-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {overriding ? '...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
